import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  updateProfile,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import { User, UserStats, MultiplayerStats } from '../types';

const googleProvider = new GoogleAuthProvider();

// Create a new user in Firestore
const createUserDocument = async (firebaseUser: FirebaseUser, displayName?: string): Promise<User> => {
  const userRef = doc(db, 'users', firebaseUser.uid);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    const initialStats: UserStats = {
      gamesPlayed: 0,
      gamesWon: 0,
      totalScore: 0,
      averageScore: 0,
      bestScore: 0,
      currentStreak: 0,
      longestStreak: 0,
      favoriteCategory: null,
      accuracyRate: 0,
    };

    const initialMultiplayerStats: MultiplayerStats = {
      gamesPlayed: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      totalScore: 0,
      averageScore: 0,
      bestScore: 0,
      winRate: 0,
      currentStreak: 0,
      longestStreak: 0,
    };

    const newUser: Omit<User, 'createdAt' | 'lastLogin'> = {
      uid: firebaseUser.uid,
      email: firebaseUser.email || '',
      displayName: displayName || firebaseUser.displayName || 'Anonymous',
      avatar: Math.floor(Math.random() * 10), // Random avatar 0-9
      xp: 0,
      rank: 'Seeker',
      stats: initialStats,
      multiplayerStats: initialMultiplayerStats,
    };

    await setDoc(userRef, {
      ...newUser,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
    });

    return {
      ...newUser,
      createdAt: new Date(),
      lastLogin: new Date(),
    };
  }

  // Update last login
  await updateDoc(userRef, {
    lastLogin: serverTimestamp(),
  });

  const userData = userSnap.data() as User;
  return userData;
};

// Get user document from Firestore
export const getUserDocument = async (uid: string): Promise<User | null> => {
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    return userSnap.data() as User;
  }

  return null;
};

// Register with email and password
export const registerWithEmail = async (
  email: string,
  password: string,
  displayName: string
): Promise<User> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    // Update display name in Firebase Auth
    await updateProfile(firebaseUser, { displayName });

    // Create user document in Firestore
    const user = await createUserDocument(firebaseUser, displayName);

    return user;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to register');
  }
};

// Sign in with email and password
export const signInWithEmail = async (email: string, password: string): Promise<User> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    // Get or create user document
    const user = await createUserDocument(firebaseUser);

    return user;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to sign in');
  }
};

// Sign in with Google
export const signInWithGoogle = async (): Promise<User> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const firebaseUser = result.user;

    // Get or create user document
    const user = await createUserDocument(firebaseUser);

    return user;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to sign in with Google');
  }
};

// Sign out
export const logout = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error: any) {
    throw new Error(error.message || 'Failed to sign out');
  }
};

// Update user profile
export const updateUserProfile = async (
  uid: string,
  updates: Partial<Omit<User, 'uid' | 'email' | 'createdAt' | 'lastLogin'>>
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, updates);
  } catch (error: any) {
    throw new Error(error.message || 'Failed to update profile');
  }
};

// Migrate user to add multiplayerStats field if missing
export const migrateUserStats = async (uid: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const userData = userSnap.data();

      // Check if multiplayerStats exists
      if (!userData.multiplayerStats) {
        console.log('🔄 Migrating user stats for:', uid);

        const initialMultiplayerStats: MultiplayerStats = {
          gamesPlayed: 0,
          wins: 0,
          losses: 0,
          draws: 0,
          totalScore: 0,
          averageScore: 0,
          bestScore: 0,
          winRate: 0,
          currentStreak: 0,
          longestStreak: 0,
        };

        await updateDoc(userRef, {
          multiplayerStats: initialMultiplayerStats,
        });

        console.log('✅ User stats migrated successfully');
      }
    }
  } catch (error: any) {
    console.error('❌ Error migrating user stats:', error);
    throw new Error(error.message || 'Failed to migrate user stats');
  }
};
